import * as React from 'react';

import { ContentCopy } from '@mui/icons-material';
import { Box, IconButton, Tab, Tabs, Tooltip } from '@mui/material';
import { cliCommand } from 'services/jobSpecs';
import type { JobInput } from 'services/jobSpecs';

import styles from './JobLauncher.module.css';
import { useCopy } from './useCopy';

/**
 * What is about to be sent, before it is sent.
 *
 * Three views of one computed object: the flags the script parses, the body
 * conchBatch receives, and the command line that reproduces the run on a box.
 * The AWS tab is the audit surface — it is where an operator can see which job
 * definition and queue this console resolved to, which is the difference
 * between a dev run and a production one.
 */

type Props = { input: JobInput };

const TABS = ['CLI command', 'AWS payload'];

const JobPreview: React.FC<Props> = ({ input }) => {
  const [tab, setTab] = React.useState(0);
  const { copied, copy, reset } = useCopy();

  // The command line leads: it is the form people actually reuse — paste it on
  // a box and the run is reproduced. The payload is the audit view behind it,
  // and it carries the flags too, so a separate Args tab said nothing new.
  const text = React.useMemo(
    () => (tab === 0 ? cliCommand(input) : JSON.stringify(input, null, 2)),
    [tab, input],
  );

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          borderBottom: '1px solid rgba(0,0,0,0.12)',
        }}
      >
        <Tabs
          value={tab}
          onChange={(_e, next) => {
            setTab(next);
            reset();
          }}
          sx={{ minHeight: 36, flexGrow: 1 }}
        >
          {TABS.map((label) => (
            <Tab
              key={label}
              label={label}
              sx={{ minHeight: 36, textTransform: 'none' }}
            />
          ))}
        </Tabs>
        <Tooltip title={copied ? 'Copied' : 'Copy'}>
          <IconButton size="small" onClick={() => copy(text)} aria-label="Copy">
            <ContentCopy fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <pre className={styles.preview}>{text}</pre>
    </Box>
  );
};

export default JobPreview;
